import React from 'react';
import { Mail, MapPin, ExternalLink, Calendar, Linkedin, Send } from 'lucide-react';
import analyticsService from '../services/analyticsService';

const Contact = () => {

  // Track contact method clicks
  const handleContactClick = (method, url, buttonText) => {
    analyticsService.trackContactMethod(method, 'contact_page');
    analyticsService.trackButtonClick(buttonText, 'contact_page', {
      contact_method: method,
      destination: url
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Professional Profile */}
      <section className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Get In Touch
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
            Whether you're interested in trading strategies, product collaboration, or just want to chat about the markets, 
            I'm always open to meaningful discussions.
          </p>
        </div>
      </section>

      {/* Professional Profile Section */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
            <div className="text-center mb-8">
              <div className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium mb-6">
                <Linkedin className="w-4 h-4 mr-2" />
                Professional Profile
              </div>
            </div>
            
            {/* LinkedIn Profile Card */}
            <div className="bg-gray-900 rounded-xl p-8 text-white mb-6">
              <div className="flex items-center space-x-6 mb-6">
                <img
                  src="/images/kunal-agarwal.jpg"
                  alt="Kunal Agarwal"
                  className="w-20 h-20 rounded-full border-4 border-white object-cover"
                />
                <div>
                  <h3 className="text-2xl font-bold">Kunal Agarwal</h3>
                  <p className="text-gray-200 mb-2">Product Manager | Trading Strategist</p>
                  <p className="text-gray-300 text-sm">500+ connections</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-gray-300" />
                  <span className="text-gray-200">India</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Briefcase className="w-5 h-5 text-gray-300" />
                  <span className="text-gray-200">Product Manager at Joveo</span>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <a
                href="https://www.linkedin.com/in/kunal35/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleContactClick('linkedin', 'https://www.linkedin.com/in/kunal35/', 'View Full Profile on LinkedIn')}
                className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors duration-200 shadow-lg"
              >
                <Linkedin className="mr-2 w-5 h-5" />
                View Full Profile on LinkedIn
                <ExternalLink className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            
            {/* Schedule Meeting */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-6">
                <Calendar className="w-6 h-6 text-gray-600" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Schedule a Meeting</h3>
              <p className="text-gray-600 mb-6 text-lg flex-grow">
                Book a 30-minute consultation to discuss your trading goals and explore collaboration opportunities.
              </p>
              <div className="mt-auto">
                <a
                  href="https://calendly.com/kunal-twoandhalfcents/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleContactClick('calendly', 'https://calendly.com/kunal-twoandhalfcents/30min', 'Schedule Meeting')}
                  className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors duration-200 shadow-lg"
                >
                  <Calendar className="mr-2 w-5 h-5" />
                  Schedule Meeting
                  <ExternalLink className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Email Information */}
            <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-6">
                <Mail className="w-6 h-6 text-gray-600" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Email Information</h3>
              <p className="text-gray-600 mb-6 text-lg flex-grow">
                Send me a detailed message about your requirements, questions, or collaboration ideas.
              </p>
              <div className="space-y-4 mt-auto">
                <div className="flex items-center space-x-3 mb-4">
                  <Mail className="w-5 h-5 text-gray-600" />
                  <a 
                    href="mailto:kunal@twoandhalfcents.com" 
                    onClick={() => handleContactClick('email', 'mailto:kunal@twoandhalfcents.com', 'kunal@twoandhalfcents.com')}
                    className="text-gray-900 hover:text-gray-700 font-medium text-lg"
                  >
                    kunal@twoandhalfcents.com
                  </a>
                </div>
                <a
                  href="mailto:kunal@twoandhalfcents.com"
                  onClick={() => handleContactClick('email', 'mailto:kunal@twoandhalfcents.com', 'Send Email')}
                  className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors duration-200 shadow-lg"
                >
                  <Send className="mr-2 w-5 h-5" />
                  Send Email
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const Briefcase = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4a2 2 0 00-2-2H8a2 2 0 00-2 2v2M4 6v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2z" />
  </svg>
);

export default Contact;